import { useEffect, useRef, type ReactNode } from 'react';
import './PickPreview.css';

/**
 * PickPreview — **왼쪽에서 고르고 오른쪽에 실제 모양** (2026-09-15 · 대시보드 시안 v2 V0 · 정본 제안 F1-3 `#PICKPREVIEW`).
 *
 * 쓰는 곳: 위젯 설정(목록 · 폼 · 설정 뒤 모양) · 새 대시보드 창(폼 · 미리보기) · 위젯 추가 창(목록 · 미리보기).
 * `Modal bodyFlat` 안에 넣는 **몸통 틀**이다 — 머리 · 발(저장 · 취소)은 `Modal` 이 그린다.
 *
 * ★★★ **미리보기는 실제 컴포넌트다 · 누를 수 없다.** 따로 그린 그림을 두면 위젯이 바뀔 때마다 그림이 어긋난다
 *     (정본 B6-2 「그림은 도해다 — 스크린샷이 아니다」와 반대 자리: 여기서는 **실물**을 보여 주는 것이 목적이다).
 *     `inert` 로 포커스 · 누르기 · 읽기를 모두 막는다 — 창 안 Tab 이 미리보기 속 단추로 새지 않게.
 * ★★ 미리보기가 무엇이 바뀌었는지는 **머리 한 줄**이 말한다(「누적 근무 뺌 · 결재 대기 위로」) — 스크린리더는 그 줄을 읽는다.
 * ★ 좁으면(창 폭이 모자라면) 세로로 쌓는다 — 목록 · 폼 · 미리보기 순서 그대로.
 */

export interface PickPreviewProps {
  /** 왼쪽 목록 — 없으면 두 칸(폼 · 미리보기). */
  list?: ReactNode;
  /** 왼쪽 목록의 이름(스크린리더). */
  listLabel?: string;
  /** 가운데 폼. */
  children: ReactNode;
  /** 오른쪽 미리보기 — 실제 컴포넌트. */
  preview: ReactNode;
  /** 미리보기 머리 — 「설정 뒤 모양」 · 「미리보기」. */
  previewTitle: string;
  /** 머리 옆 한 줄 — 무엇이 바뀌었나. */
  previewNote?: ReactNode;
  className?: string;
}

export function PickPreview({
  list, listLabel, children, preview, previewTitle, previewNote, className,
}: PickPreviewProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  // React 18 은 `inert` 를 prop 으로 모른다 — 속성으로 직접 단다.
  useEffect(() => { frameRef.current?.setAttribute('inert', ''); }, []);

  return (
    <div className={['wc-pickpv', list ? 'wc-pickpv--3' : 'wc-pickpv--2', className ?? ''].filter(Boolean).join(' ')}>
      {list && <nav className="wc-pickpv__list" aria-label={listLabel}>{list}</nav>}
      <div className="wc-pickpv__main">{children}</div>
      <section className="wc-pickpv__side" aria-label={previewTitle}>
        <header className="wc-pickpv__head">
          <b>{previewTitle}</b>
          {previewNote != null && <span className="wc-pickpv__note" role="status">{previewNote}</span>}
        </header>
        <div ref={frameRef} className="wc-pickpv__frame">{preview}</div>
      </section>
    </div>
  );
}
